
  import { defineComponent } from 'vue';
  import { mapActions, mapGetters } from 'vuex';
  import axios from 'axios';

  import Constants from '@/common/constants';

  import { USER_GROUPS_MESSAGE } from '../../constants/alert';
  import { USER_GROUPS_ACTIONS_NAME, USER_GROUPS_GETTERS_NAME } from '../../store/userGroups/name';
  import { VED_COMMON_ACTIONS_NAME, VED_COMMON_GETTERS_NAME } from '../../store/vedCommon/name';
  import { TCustomsOrgansOnBorderItem } from '../../types/common-aggregated';
  import { TUserGroupListsModalTreeListUpdateGroupCustom } from '../../types/user-group-lists-modal';
  import { collectSubtreeIds, findNodeById } from '../../utils/fts/user-groups/tree-helpers';
  import BaseListsModal from './BaseListsModal.vue';
  import { TUserGroupListConfig } from './list/types';
  import AddEditTreeGroupModal from './modals/AddEditTreeGroupModal.vue';

  export default defineComponent({
    name: 'UserCustomsHouseListsModal',
    components: { BaseListsModal, AddEditTreeGroupModal },
    props: { showModal: { type: Boolean, default: false } },
    emits: ['close', 'deletedSubtreeIds'],
    data(): {
      selectedGroup: number | null;
      selectedItems: string[];
      showAddEditModal: boolean;
      editingGroup: TCustomsOrgansOnBorderItem | null;
      loading: boolean;
      loadingLinks: boolean;
      linksAbortController: AbortController | null;
    } {
      return {
        selectedGroup: null,
        selectedItems: [],
        showAddEditModal: false,
        editingGroup: null,
        loading: false,
        loadingLinks: false,
        linksAbortController: null,
      };
    },
    computed: {
      ...mapGetters({
        customsGroups: VED_COMMON_GETTERS_NAME.getCustomsOrgansOnBorder,
        customsItems: USER_GROUPS_GETTERS_NAME.getCustomsItems,
      }),
      leftConfig(): TUserGroupListConfig {
        return {
          items: this.customsGroups,
          viewMode: 'treeSingle',
          listId: 'left-groups',
          loading: this.loading,
          showExpandControls: this.hasExpandableLeftGroups,
          key: 'left-groups',
        };
      },
      rightConfig(): TUserGroupListConfig {
        return {
          items: this.customsItems,
          viewMode: 'flatMultiple',
          listId: 'right-tree',
          loading: this.loading || this.loadingLinks,
          key: this.customsItems.length,
        };
      },
      isRootSelected(): boolean {
        return this.selectedGroup === -1;
      },
      hasExpandableLeftGroups(): boolean {
        const hasChildren = (groups: TCustomsOrgansOnBorderItem[]): boolean =>
          groups.some((g) => (g.children?.length ?? 0) > 0);
        return hasChildren(this.customsGroups);
      },
    },
    created() {
      this.loadData();
    },
    watch: {
      selectedGroup: {
        async handler(newGroupId: number | null) {
          if (!newGroupId) {
            this.selectedItems = [];
            return;
          }
          await this.loadLinksForGroup(newGroupId);
        },
        immediate: true,
      },
    },
    methods: {
      ...mapActions({
        searchGroupsAction: VED_COMMON_ACTIONS_NAME.searchCustomsOrgansOnBorder,
        searchCustomsOrgansOnBorderTree: VED_COMMON_ACTIONS_NAME.searchCustomsOrgansOnBorderTree,
        searchCustomsItems: USER_GROUPS_ACTIONS_NAME.searchCustomsItems,
        searchCustomsLinks: USER_GROUPS_ACTIONS_NAME.searchCustomsLinks,
        createGroupAction: USER_GROUPS_ACTIONS_NAME.createCustomsGroup,
        updateGroupAction: USER_GROUPS_ACTIONS_NAME.updateCustomsGroup,
        deleteGroupAction: USER_GROUPS_ACTIONS_NAME.deleteCustomsGroup,
        saveGroupItemsAction: USER_GROUPS_ACTIONS_NAME.saveCustomsGroupItems,
      }),

      onUpdateLeft(value: number | null) {
        this.selectedGroup = value !== null ? Number(value) : null;
      },
      onUpdateRight(value: string[] | null) {
        this.selectedItems = value || [];
      },

      getFirstGroupId(): number | null {
        if (this.customsGroups.length === 0) return null;
        const firstNonRoot = this.customsGroups.find((g: TCustomsOrgansOnBorderItem) => g.parent !== -1);
        return firstNonRoot ? firstNonRoot.id : null;
      },

      async loadData(): Promise<void> {
        this.loading = true;
        try {
          const [groupsResult, itemsResult] = await Promise.all([
            await this.searchGroupsAction(),
            await this.searchCustomsItems(),
          ]);

          if (!groupsResult?.success) {
            Constants.alert.fire({
              icon: 'error',
              title: USER_GROUPS_MESSAGE.TITLE_ERROR,
              text: USER_GROUPS_MESSAGE.COMMON.GET_GROUPS_FAILED,
            });
          }

          if (!itemsResult?.success) {
            Constants.alert.fire({
              icon: 'error',
              title: USER_GROUPS_MESSAGE.TITLE_ERROR,
              text: USER_GROUPS_MESSAGE.CUSTOMS.GET_ITEMS_FAILED,
            });
          }

          this.selectedGroup = this.getFirstGroupId();
        } finally {
          this.loading = false;
        }
      },

      async loadLinksForGroup(groupId: number): Promise<void> {
        if (this.linksAbortController) {
          this.linksAbortController.abort();
        }
        const abortController = new AbortController();
        this.linksAbortController = abortController;
        this.loadingLinks = true;

        try {
          const result = await this.searchCustomsLinks({ groupId, abortController });
          if (!result?.success && !abortController.signal.aborted) {
            Constants.alert.fire({
              icon: 'error',
              title: USER_GROUPS_MESSAGE.TITLE_ERROR,
              text: USER_GROUPS_MESSAGE.COMMON.LINKS_LOAD_FAILED,
            });
          }
          if (!result?.success) return;
          this.selectedItems = result.data;
        } finally {
          this.loadingLinks = false;
          if (this.linksAbortController === abortController) {
            this.linksAbortController = null;
          }
        }
      },

      handleAdd(): void {
        this.editingGroup = null;
        this.showAddEditModal = true;
      },
      handleEdit(): void {
        if (this.isRootSelected || !this.selectedGroup) return;
        const node = findNodeById(this.customsGroups, this.selectedGroup);
        if (node) {
          this.editingGroup = {
            id: node.id,
            name: node.name,
            kod: node.kod || '',
            parent: node.parent,
            children: undefined,
          };
          this.showAddEditModal = true;
        }
      },
      async handleDelete(): Promise<void> {
        if (!this.selectedGroup || this.isRootSelected) return;
        this.loading = true;
        try {
          const subtreeIds = collectSubtreeIds(this.customsGroups, this.selectedGroup);
          const result = await this.deleteGroupAction(this.selectedGroup);
          if (!result?.success) throw new Error();
          this.$emit('deletedSubtreeIds', subtreeIds);
          this.selectedGroup = this.getFirstGroupId();
          Constants.alert.fire({
            icon: 'success',
            title: USER_GROUPS_MESSAGE.TITLE_SUCCESS,
            text: USER_GROUPS_MESSAGE.COMMON.DELETE_GROUP_SUCCESS,
          });
          this.searchGroupsAction();
          this.searchCustomsOrgansOnBorderTree();
        } catch {
          Constants.alert.fire({
            icon: 'error',
            title: USER_GROUPS_MESSAGE.TITLE_ERROR,
            text: USER_GROUPS_MESSAGE.COMMON.DELETE_GROUP_FAILED,
          });
        } finally {
          this.loading = false;
        }
      },

      async handleSaveGroup(payload: TUserGroupListsModalTreeListUpdateGroupCustom): Promise<void> {
        this.loading = true;
        try {
          if (payload.id) {
            const result = await this.updateGroupAction(payload);
            if (!result?.success) throw new Error();
            Constants.alert.fire({
              icon: 'success',
              title: USER_GROUPS_MESSAGE.TITLE_SUCCESS,
              text: USER_GROUPS_MESSAGE.COMMON.UPDATE_GROUP_SUCCESS,
            });
          } else {
            const result = await this.createGroupAction(payload);
            if (!result?.success) throw new Error();
            this.selectedGroup = result.data.id;
            Constants.alert.fire({
              icon: 'success',
              title: USER_GROUPS_MESSAGE.TITLE_SUCCESS,
              text: USER_GROUPS_MESSAGE.COMMON.CREATE_GROUP_SUCCESS,
            });
          }

          this.searchGroupsAction();
          this.searchCustomsOrgansOnBorderTree();
        } catch (error: unknown) {
          let errorMessage = payload.id
            ? USER_GROUPS_MESSAGE.COMMON.UPDATE_GROUP_FAILED
            : USER_GROUPS_MESSAGE.COMMON.CREATE_GROUP_FAILED;
          if (axios.isAxiosError(error)) errorMessage = error.message || errorMessage;
          Constants.alert.fire({
            icon: 'error',
            title: USER_GROUPS_MESSAGE.TITLE_ERROR,
            text: errorMessage,
          });
        } finally {
          this.loading = false;
          this.showAddEditModal = false;
        }
      },

      async handleSave(): Promise<void> {
        if (this.isRootSelected) {
          Constants.alert.fire({
            icon: 'warning',
            title: USER_GROUPS_MESSAGE.TITLE_ERROR,
            text: USER_GROUPS_MESSAGE.COMMON.ROOT_GROUP_SAVE_FORBIDDEN,
          });
          return;
        }
        if (!this.selectedGroup) {
          Constants.alert.fire({
            icon: 'warning',
            title: USER_GROUPS_MESSAGE.TITLE_ERROR,
            text: USER_GROUPS_MESSAGE.COMMON.NO_GROUP_SELECTED,
          });
          return;
        }
        this.loading = true;
        try {
          const result = await this.saveGroupItemsAction({ groupId: this.selectedGroup, itemIds: this.selectedItems });
          if (!result?.success) throw new Error();
          Constants.alert.fire({
            icon: 'success',
            title: USER_GROUPS_MESSAGE.TITLE_SUCCESS,
            text: USER_GROUPS_MESSAGE.COMMON.SAVE_LINKS_SUCCESS,
          });
          this.searchGroupsAction();
          this.searchCustomsOrgansOnBorderTree();
        } catch {
          Constants.alert.fire({
            icon: 'error',
            title: USER_GROUPS_MESSAGE.TITLE_ERROR,
            text: USER_GROUPS_MESSAGE.COMMON.SAVE_LINKS_FAILED,
          });
        } finally {
          this.loading = false;
        }
      },
    },
  });
